import api from './api';
import {
  Role,
  RoleListItem,
  Permission,
  PermissionCategory,
  CreateRoleRequest,
  UpdateRoleRequest,
  UpdatePermissionsRequest,
  ApiResponse,
} from '../types/role';

export const roleService = {
  /**
   * Get all roles
   * @param isSystemRole - Filter by system role (true/false/undefined for all)
   */
  async getAll(isSystemRole?: boolean): Promise<ApiResponse<RoleListItem[]>> {
    const params = new URLSearchParams();
    if (isSystemRole !== undefined) {
      params.append('isSystemRole', String(isSystemRole));
    }
    const queryString = params.toString();
    const url = queryString ? `/api/v1/roles?${queryString}` : '/api/v1/roles';

    const { data } = await api.get<ApiResponse<RoleListItem[]>>(url);
    return data;
  },

  /**
   * Get role by ID with permissions
   */
  async getById(id: string): Promise<ApiResponse<Role>> {
    const { data } = await api.get<ApiResponse<Role>>(`/api/v1/roles/${id}`);
    return data;
  },

  /**
   * Create a new role
   */
  async create(request: CreateRoleRequest): Promise<ApiResponse<Role>> {
    const { data } = await api.post<ApiResponse<Role>>('/api/v1/roles', request);
    return data;
  },

  /**
   * Update role
   */
  async update(id: string, request: UpdateRoleRequest): Promise<ApiResponse<Role>> {
    const { data } = await api.put<ApiResponse<Role>>(`/api/v1/roles/${id}`, request);
    return data;
  },

  /**
   * Update role permissions only
   */
  async updatePermissions(id: string, request: UpdatePermissionsRequest): Promise<ApiResponse<null>> {
    const { data } = await api.put<ApiResponse<null>>(`/api/v1/roles/${id}/permissions`, request);
    return data;
  },

  /**
   * Delete role
   */
  async delete(id: string): Promise<ApiResponse<null>> {
    const { data } = await api.delete<ApiResponse<null>>(`/api/v1/roles/${id}`);
    return data;
  },

  /**
   * Get all permissions
   */
  async getAllPermissions(): Promise<ApiResponse<Permission[]>> {
    const { data } = await api.get<ApiResponse<Permission[]>>('/api/v1/permissions');
    return data;
  },

  /**
   * Get permissions grouped by category
   */
  async getPermissionsByCategory(): Promise<ApiResponse<PermissionCategory[]>> {
    const { data } = await api.get<ApiResponse<PermissionCategory[]>>('/api/v1/permissions/categories');
    return data;
  },

  /**
   * Get system roles only
   */
  async getSystemRoles(): Promise<ApiResponse<RoleListItem[]>> {
    return this.getAll(true);
  },

  /**
   * Get customer roles only
   */
  async getCustomerRoles(): Promise<ApiResponse<RoleListItem[]>> {
    return this.getAll(false);
  },
};
